import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

import { flowActions, State, StaticRole } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Alert, Box, Chip, CircularProgress, Stack } from '@mui/material';
import { LayoutWorkspace, LayoutSidebar } from '@common/layout';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { Table } from '@common/Table';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GridActionsCellItem, GridClearIcon, GridValueFormatterParams } from '@mui/x-data-grid';
import { listWorkspaceRoles, listWorkspaceUsers } from '@requests/roles';
import { PermissionRoles } from './PermissionRoles';
import { DeletePermissionGroup } from '@components/Modals/DeletePermissionGroup';
import { Resource, WorkspaceUser } from '@store/types';
import { Select } from '@common/Select';
import { assignWorkspaceRole } from '@requests/users';
import { useSnackbar } from 'notistack';

import { Edit, Person2Outlined, RemoveModeratorOutlined, SaveOutlined, SecurityOutlined } from '@mui/icons-material';
import { AssignWorkspaceAdmin } from '@components/Modals/AssignWorkspaceAdmin';
import { RemoveWorkspaceAdmin } from '@components/Modals/RemoveWorkspaceAdmin';
import { usePermissions } from '@common/hooks/usePermissions';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 61px);
`;

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  height: calc(100vh - 61px);
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 2rem;
  justify-content: center;
  width: 100%;
`;

const userType: { [key: string]: string } = {
  organization_sub_user: 'User',
  organization_owner: 'Owner',
};

export const PermissionGroups: React.FC = () => {
  const dispatch = useDispatch();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { admin } = usePermissions(service_id as string, Resource.ADMIN);

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);
  const orgId = useSelector((state: State) => state.auth.org_id);

  const [editing, setEditing] = useState<string | null>(null);
  const [updatedRole, setUpdatedRole] = useState<string | null>(null);

  const {
    data: users,
    isLoading: isRolesLoading,
    isFetching: isRolesFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ['roles', { serviceId: service_id }],
    queryFn: listWorkspaceUsers,
    staleTime: 30000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: Boolean(service_id),
  });

  let errorMessage = '';
  if (axios.isAxiosError(error)) errorMessage = error.response?.data?.error || error.message;
  else errorMessage = error?.message || '';

  const { data: groups = [] } = useQuery({
    queryKey: ['groups', { serviceId: service_id, orgId }],
    queryFn: listWorkspaceRoles,
    staleTime: 30000,
    enabled: Boolean(service_id && orgId),
  });

  const { mutateAsync: assign, isPending: isAssignPending } = useMutation({
    mutationFn: assignWorkspaceRole,
    onMutate: () => {
      enqueueSnackbar('Assigning user permission group..', { variant: 'info', autoHideDuration: 5000 });
    },
    onSuccess: async () => {
      await refetch();
      setEditing(null);
      setUpdatedRole(null);
      enqueueSnackbar('Permission group assigned successfully.', { variant: 'success', autoHideDuration: 5000 });
    },
  });

  const toggleEditing = (row: WorkspaceUser) => {
    if (editing) {
      const serviceId = service_id as string;
      const userId = row.id;
      const roleId = updatedRole === 'view_only' ? null : updatedRole;

      if (roleId === row.role_id || updatedRole === null) return cancelEditing();

      assign({ serviceId, userId, roleId });
    } else setEditing(row.id);
  };

  const toggleAdmin = (row: WorkspaceUser) => {
    const isAdmin = row.role_id === StaticRole.Admin;
    if (isAdmin) dispatch(flowActions.setRemoveWorkspaceAdmin(row.id));
    else dispatch(flowActions.setAssignWorkspaceAdmin(row.id));
  };

  const cancelEditing = () => {
    setEditing(null);
    setUpdatedRole(null);
  };

  const columns = [
    {
      field: 'email',
      headerName: 'User',
      flex: 300,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => {
        return (
          <Stack direction='row' alignItems='center'>
            <Person2Outlined />
            <Box mr='12px' />
            {params?.value}
          </Stack>
        );
      },
    },
    {
      field: 'record_type',
      headerName: 'Type',
      flex: 75,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => {
        const index = params.value;
        return userType[index];
      },
    },
    {
      field: 'role_name',
      headerName: 'Permission Group',
      flex: 110,
      sortable: true,
      renderCell: (params: any) => {
        const isEditing = editing === params.id;
        const isAdmin = params.row.role_id === StaticRole.Admin;
        const isOrgOwner = params.row.id === orgId;

        if (isEditing) {
          const defaultOptions = [{ value: 'view_only', label: 'View Only' }];

          const options = [...defaultOptions, ...groups.map((group) => ({ label: group.role_name, value: group.id }))];
          const group = groups.find((group) => group.role_name === params?.value);

          return (
            <Select
              value={updatedRole || group?.id || 'view_only'}
              setValue={(value) => setUpdatedRole(value)}
              options={options}
              disabled={isAssignPending}
            />
          );
        }

        return (
          <Stack>
            <Chip
              label={isAdmin || isOrgOwner ? 'Admin' : params.value || 'View Only'}
              size='small'
              color={isAdmin || isOrgOwner ? 'success' : 'info'}
              variant='outlined'
              sx={{ height: 1 }}
            />
          </Stack>
        );
      },
    },
    admin && {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 140,
      cellClassName: 'actions',
      getActions: ({ row }: { row: WorkspaceUser }) => {
        const isAdmin = row.role_id === StaticRole.Admin;
        const isEditing = editing === row.id;
        const isEditingOtherRow = Boolean(editing !== null && editing !== row.id);
        const isOrgOwner = row.id === orgId;

        return [
          <GridActionsCellItem
            key='edit'
            icon={isEditing ? isAssignPending ? <CircularProgress size={14} /> : <SaveOutlined /> : <Edit />}
            label='Edit'
            disabled={isOrgOwner || isAdmin || isEditingOtherRow || isAssignPending}
            onClick={() => toggleEditing(row)}
            color='inherit'
          />,
          isEditing && !isAssignPending && (
            <GridActionsCellItem
              key='stop'
              icon={<GridClearIcon />}
              label='Edit'
              onClick={cancelEditing}
              color='inherit'
            />
          ),
          !isEditing && (
            <GridActionsCellItem
              key='edit'
              icon={isAdmin ? <RemoveModeratorOutlined /> : <SecurityOutlined />}
              label='Edit'
              disabled={isOrgOwner || isEditingOtherRow || isAssignPending}
              onClick={() => toggleAdmin(row)}
              color='inherit'
            />
          ),
        ].filter(Boolean);
      },
    },
  ].filter(Boolean);

  const body = (
    <LayoutSidebar
      body={
        <Table
          // page width - navbar - sidebar - sidebar padding
          width='calc(100vw - 61px - 240px - 400px)'
          height='calc(100vh - 61px)'
          data={users}
          columns={columns}
          density={'standard'}
          sortModel='date_created'
          getRowId={(row) => row.id}
          loading={isRolesLoading || isRolesFetching}
        />
      }
      rightSidebar={<PermissionRoles />}
    />
  );

  const errorAlert = (
    <LayoutSidebar
      body={
        <ErrorContainer>
          <Alert severity='info'>{errorMessage}</Alert>
        </ErrorContainer>
      }
      rightSidebar={<PermissionRoles />}
    />
  );

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <LayoutContainer>
          <ServiceNavigation />
          <DeletePermissionGroup />
          <AssignWorkspaceAdmin />
          <RemoveWorkspaceAdmin />
          <Container>{error ? errorAlert : body}</Container>
        </LayoutContainer>
      }
    />
  );
};
